/**
 * Symbol extractor backed by the TypeScript compiler API — a proper AST, not
 * regex (Blueprint v2 §3.5). Handles .ts/.tsx/.mts/.cts and .js/.jsx/.mjs/.cjs.
 * `typescript` is already a workspace dependency, so this adds no new runtime
 * weight beyond what the repo already ships; other languages get their own
 * extractor (web-tree-sitter, D5) behind {@link SymbolExtractor}.
 */
import * as ts from 'typescript';
import type { SymbolDef, SymbolExtractor, SymbolKind } from './symbols.js';

const TS_EXTENSIONS = ['.ts', '.tsx', '.mts', '.cts', '.js', '.jsx', '.mjs', '.cjs'] as const;

function scriptKindFor(path: string): ts.ScriptKind {
  if (path.endsWith('.tsx')) return ts.ScriptKind.TSX;
  if (path.endsWith('.jsx')) return ts.ScriptKind.JSX;
  if (path.endsWith('.js') || path.endsWith('.mjs') || path.endsWith('.cjs')) return ts.ScriptKind.JS;
  return ts.ScriptKind.TS;
}

/** Flatten an identifier or a destructuring pattern into its bound names. */
function bindingNames(name: ts.BindingName): string[] {
  const out: string[] = [];
  const walk = (n: ts.BindingName): void => {
    if (ts.isIdentifier(n)) {
      out.push(n.text);
      return;
    }
    for (const el of n.elements) {
      if (ts.isOmittedExpression(el)) continue;
      walk(el.name);
    }
  };
  walk(name);
  return out;
}

export class TypeScriptSymbolExtractor implements SymbolExtractor {
  supports(path: string): boolean {
    return TS_EXTENSIONS.some((ext) => path.endsWith(ext));
  }

  extract(path: string, content: string): SymbolDef[] {
    const sf = ts.createSourceFile(
      path,
      content,
      ts.ScriptTarget.Latest,
      /* setParentNodes */ true,
      scriptKindFor(path),
    );
    const out: SymbolDef[] = [];
    // Local names exported via a bare `export { x }` statement (no `from`), so a
    // post-pass can flip their declaration's `exported` flag to true.
    const exportedLocals = new Set<string>();
    const lineOf = (pos: number): number => sf.getLineAndCharacterOfPosition(pos).line + 1;

    const push = (
      name: string,
      kind: SymbolKind,
      node: ts.Node,
      exported: boolean,
      signature: string,
      container?: string,
    ): void => {
      out.push({
        name,
        kind,
        signature: signature.replace(/\s+/g, ' ').trim().slice(0, 200),
        file: path,
        startLine: lineOf(node.getStart(sf)),
        endLine: lineOf(node.getEnd()),
        exported,
        ...(container ? { container } : {}),
      });
    };

    const isExported = (node: ts.Node): boolean => {
      const mods = ts.canHaveModifiers(node) ? ts.getModifiers(node) : undefined;
      return (
        mods?.some(
          (m) =>
            m.kind === ts.SyntaxKind.ExportKeyword || m.kind === ts.SyntaxKind.DefaultKeyword,
        ) ?? false
      );
    };

    const params = (ps: ts.NodeArray<ts.ParameterDeclaration>): string =>
      `(${ps.map((p) => p.getText(sf)).join(', ')})`;

    const visit = (node: ts.Node, container?: string): void => {
      // `export { a, b }` / `export { orig as alias }` / `export { x } from './y'`.
      if (ts.isExportDeclaration(node)) {
        const clause = node.exportClause;
        if (clause && ts.isNamedExports(clause)) {
          for (const spec of clause.elements) {
            if (!ts.isIdentifier(spec.name)) continue;
            if (node.moduleSpecifier) {
              // Re-export from another module → a new alias surfaced here.
              push(spec.name.text, 'variable', spec, true, spec.name.text, container);
            } else {
              // Bare re-export → the LOCAL declaration is the exported one.
              const local =
                spec.propertyName && ts.isIdentifier(spec.propertyName)
                  ? spec.propertyName.text
                  : spec.name.text;
              exportedLocals.add(local);
            }
          }
        }
        return;
      }
      if (ts.isFunctionDeclaration(node) && node.name) {
        const ret = node.type ? `: ${node.type.getText(sf)}` : '';
        push(node.name.text, 'function', node, isExported(node), `${node.name.text}${params(node.parameters)}${ret}`, container);
        return;
      }
      if (ts.isClassDeclaration(node) && node.name) {
        const className = node.name.text;
        push(className, 'class', node, isExported(node), `class ${className}`, container);
        node.members.forEach((m) => visit(m, className));
        return;
      }
      if (ts.isInterfaceDeclaration(node)) {
        push(node.name.text, 'interface', node, isExported(node), `interface ${node.name.text}`, container);
        return;
      }
      if (ts.isTypeAliasDeclaration(node)) {
        push(node.name.text, 'type', node, isExported(node), `type ${node.name.text}`, container);
        return;
      }
      if (ts.isEnumDeclaration(node)) {
        const enumName = node.name.text;
        const exported = isExported(node);
        push(enumName, 'enum', node, exported, `enum ${enumName}`, container);
        for (const m of node.members) {
          if (ts.isIdentifier(m.name)) {
            push(m.name.text, 'property', m, exported, `${enumName}.${m.name.text}`, enumName);
          }
        }
        return;
      }
      if (ts.isModuleDeclaration(node) && ts.isIdentifier(node.name)) {
        const nsName = node.name.text;
        push(nsName, 'namespace', node, isExported(node), `namespace ${nsName}`, container);
        if (node.body && ts.isModuleBlock(node.body)) {
          node.body.statements.forEach((s) => visit(s, nsName));
        }
        return;
      }
      if (ts.isVariableStatement(node)) {
        const exported = isExported(node);
        for (const decl of node.declarationList.declarations) {
          if (ts.isIdentifier(decl.name)) {
            const init = decl.initializer;
            if (init && (ts.isArrowFunction(init) || ts.isFunctionExpression(init))) {
              const ret = init.type
                ? `: ${init.type.getText(sf)}`
                : decl.type
                  ? `: ${decl.type.getText(sf)}`
                  : '';
              push(decl.name.text, 'function', decl, exported, `${decl.name.text}${params(init.parameters)}${ret}`, container);
            } else {
              const typeSig = decl.type ? `: ${decl.type.getText(sf)}` : '';
              push(decl.name.text, 'variable', decl, exported, `${decl.name.text}${typeSig}`, container);
            }
          } else {
            // Destructuring: `const { a, b } = ...` / `const [x, y] = ...`.
            for (const bound of bindingNames(decl.name)) {
              push(bound, 'variable', decl, exported, bound, container);
            }
          }
        }
        return;
      }
      if (container && ts.isMethodDeclaration(node) && ts.isIdentifier(node.name)) {
        const ret = node.type ? `: ${node.type.getText(sf)}` : '';
        push(node.name.text, 'method', node, false, `${node.name.text}${params(node.parameters)}${ret}`, container);
        return;
      }
      if (container && (ts.isGetAccessor(node) || ts.isSetAccessor(node)) && ts.isIdentifier(node.name)) {
        const kw = ts.isGetAccessor(node) ? 'get' : 'set';
        const ret = node.type ? `: ${node.type.getText(sf)}` : '';
        push(node.name.text, 'method', node, false, `${kw} ${node.name.text}${params(node.parameters)}${ret}`, container);
        return;
      }
      if (container && ts.isPropertyDeclaration(node) && ts.isIdentifier(node.name)) {
        const typeSig = node.type ? `: ${node.type.getText(sf)}` : '';
        push(node.name.text, 'property', node, false, `${node.name.text}${typeSig}`, container);
        return;
      }
      // Unhandled node: descend (reaches top-level stmts, block bodies, etc.).
      ts.forEachChild(node, (c) => visit(c, container));
    };

    visit(sf);

    // A declaration named in a bare `export { x }` is exported even though its own
    // modifiers don't say so.
    if (exportedLocals.size === 0) return out;
    return out.map((d) =>
      !d.exported && exportedLocals.has(d.name) ? { ...d, exported: true } : d,
    );
  }
}
